import {
    EzBadParamException
} from '/ezlibrary/exceptions/EzExceptions.js';

import {
    EzObject,
    EzBoolean,
    EzNumber,
    EzString,
    EzPromise,
    EzJson,
    EzUrl,
    EzHtml
} from '/ezlibrary/helpers/EzHelpers.js';

import {
    EzRegistrationState
} from '/ezlibrary/enums/EzEnumerations.js';

import { EzClass } from '/ezlibrary/EzClass.js';

import { EzUserRole } from '/ezlibrary/EzUserRole.js';

import { EzHttpHelper } from '/public/javascript/common/ezclocker-http-helper.js';
import { EzNavigation } from '/public/javascript/common/ezclocker-navigation-helper.js';

/**
 * @class
 * @extends {EzClass}
 * @description
 * Provides access to ezClockers Account APIs
 * ~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~
 * Import with:
 *     import { EzAccountServices } from '/public/javascript/services/ezclocker-account-services.js';
 * ~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~
 *     globalThis.ezApi.ezclocker[EzAccountServices.ezApiName] &&
 *     globalThis.ezApi.ezclocker[EzAccountServices.ezApiName].ready
 * ~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~
 *     EzClass.ezWaitReady(
 *         EzAccountServices.ezEventNames.onReady,
 *         this.#ezRegistrator);
 * ~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~
 */
export class EzAccountServices extends EzClass {
    /**
     * @public @method @field
     * @type {object}
     */
    user = null;

    /**
     * @public @method @field
     * @type {object}
     */
    currentUserInfo = null;

    /**
     * @public @method @readonly @property
     */
    get EZ_GENERATED_AUTOMATIC_SIGN_IN_FORM_ID() {
        return 'EzGeneratedAutomaticSignInForm';
    }

    /**
     * @protected @method
     */
    ezInit() {
        return EzAccountServices.ezInstance;
    }

    /**
     * @deprecated Migrate to using EzClockerContext to obtain the user account
     * @public @method
     * Loads information for the currently logged in user
     * @returns {promise}
     */
    loadCurrentUser() {
        const lastKnownUser = EzAccountServices.ezInstance.user;

        EzAccountServices.ezInstance.user = null;

        return EzPromise.promise(
            (resolve, reject) => globalThis.ezApi.ezclocker.ezHttpHelper.ezGet(
                globalThis.ezApi.ezclocker.ezNavigation.getInternalApiUrl('account/user', 'v1'))
                .then(
                    globalThis.ezApi.ezclocker.ezHttpHelper.ezProcessApiResolve,
                    globalThis.ezApi.ezclocker.ezHttpHelper.ezProcessApiReject)
                .then(
                    (response) => {
                        EzAccountServices.ezInstance.user = response;
                        return resolve(EzAccountServices.ezInstance.user, lastKnownUser);
                    },
                    (eResponse) => reject(eResponse, lastKnownUser)
                ));
    }

    /**
     * @deprecated Migrate to using EzClockerContext to obtain the user account
     * @public @method
     * Obtains the current User account
     * @param {Function|null} success
     * @param {Failure|null} failure
     */
    currentUser(success, failure) {
        const lastKnownUser = EzAccountServices.ezInstance.user;

        EzAccountServices.ezInstance.user = null;

        const url = globalThis.ezApi.ezclocker.ezNavigation.getInternalApiUrl('account/user', 'v1');

        globalThis.ezApi.ezclocker.ezHttpHelper.get(url)
            .then(
                (response) => {
                    if (globalThis.ezApi.ezclocker.ezHttpHelper.isErrorResponse(response)) {
                        const em = globalThis.ezApi.ezclocker.ezHttpHelper.extractErrorResponseMessage(response);

                        globalThis.ezApi.ezclocker.ezLogger.error(em);

                        return globalThis.ezApi.ezCallback(failure, response);
                    }

                    EzAccountServices.ezInstance.user = response;

                    return globalThis.ezApi.ezCallback(success, EzAccountServices.ezInstance.user, lastKnownUser);
                },
                (eResponse) => {
                    const em = globalThis.ezApi.ezclocker.ezHttpHelper.extractErrorResponseMessageOrDefault(
                        eResponse,
                        'Unable to obtain developer token for logged in user.');

                    globalThis.ezApi.ezclocker.ezLogger.error(em);

                    return globalThis.ezApi.ezCallback(failure, eResponse, lastKnownUser);
                });
    }

    /**
     * @public @method
     * Obtains the user information for the provied employeeId
     * @returns {promise}
     * Promise.resolve result: {
     *     errorCode: 0,
     *     message: 'success',
     *     entity: {EzNovaUser}
     * }
     * Promise.reject result: {
     *     errorCode: {error_number},
     *     message: {error_message_string},
     *     entity: {null|EzNovaUser}
     * }
     */
    ezGetEmployeeUserInfo(employeeId) {
        if (!EzNumber.isNumber(employeeId)) {
            throw new EzBadParamException(
                'employeeId',
                EzAccountServices.ezInstance,
                EzAccountServices.ezInstance.ezGetEmployeeUserInfo);
        }

        return globalThis.ezApi.ezclocker.ezHttpHelper.ezGet(
            globalThis.ezApi.ezclocker.ezNavigation.ezGetInternalApiUrl(`account/employee/${employeeId}/user-info`))
            .then(
                globalThis.ezApi.ezclocker.ezServices.ezProcessApiResponse,
                globalThis.ezApi.ezclocker.ezHttpHelper.ezProcessApiReject);
    }

    /**
     * @public @method
     * Returns employee information for the logged in user.
     * @deprecated Use globalThis.ezApi.ezclocker.ezAccountServices.loggedInUserEmployeeInfo instead
     */
    loggedInUserInfo() {
        return EzPromise.promise(
            (resolve, reject) => {
                const lastCurrentUserInfo = EzAccountServices.ezInstance.currentUserInfo;

                EzAccountServices.ezInstance.currentUserInfo = null;

                const postUrl = globalThis.ezApi.ezclocker.ezNavigation.getInternalClassicServiceUrl('employee/getEmployeeInfo');

                globalThis.ezApi.ezclocker.ezHttpHelper.ezPost(postUrl, EzString.EMPTY)
                    .then(
                        (response) => {
                            if (response.errorCode !== 0) {
                                globalThis.ezApi.ezclocker.ezLogger.error(
                                    EzString.em`
                                    Unable to retrieve user information from the ezClocker service.
                                    <p>Additional Info: [${response.message}]</p>`);

                                return reject(response, lastCurrentUserInfo);
                            }

                            EzAccountServices.ezInstance.currentUserInfo = response.employeeInformation;

                            return resolve(EzAccountServices.ezInstance.currentUserInfo);
                        },
                        (eResponse) => {
                            globalThis.ezApi.ezclocker.ezLogger.error(
                                globalThis.ezApi.ezEm`
                                Unable to retrieve user information from the ezClocker service.
                                <p>Additional Info: [${eResponse.message}]</p>`);

                            return reject(eResponse, lastCurrentUserInfo);
                        }
                    );
            });
    }

    /**
     * @public @method
     * Returns employee information for the logged in user.
     * @returns {promise}
     */
    loggedInUserEmployeeInfo() {
        return EzPromise.promise(
            (resolve, reject) => {
                const lastCurrentUserInfo = EzAccountServices.ezInstance.currentUserInfo;

                EzAccountServices.ezInstance.currentUserInfo = null;

                const postUrl = globalThis.ezApi.ezclocker.ezNavigation.getInternalClassicServiceUrl('employee/getEmployeeInfo');

                globalThis.ezApi.ezclocker.ezHttpHelper.ezPost(postUrl, EzString.EMPTY)
                    .then(
                        (response) => {
                            if (response.errorCode !== 0) {
                                globalThis.ezApi.ezclocker.ezLogger.error(
                                    EzString.em`
                                        Unable to retrieve user information from the ezClocker service.
                                        <p>Additional Info: [${response.message}]</p>`);
                                return reject(response, lastCurrentUserInfo);
                            }

                            EzAccountServices.ezInstance.currentUserInfo = response.employeeInformation;

                            return resolve(EzAccountServices.ezInstance.currentUserInfo);
                        },
                        (eResponse) => {
                            globalThis.ezApi.ezclocker.ezLogger.error(
                                EzString.em`
                                    Unable to retrieve user information from the ezClocker service.
                                    <p>Additional Info: [${eResponse.message}]</p>`);

                            return reject(eResponse, lastCurrentUserInfo);
                        });
            });
    }

    /**
     * @public @method
     * Signs up for the personal app
     * @returns {promise}
     */
    ezSignUpPersonal(email, password, mobile, affiliateId) {
        const url = globalThis.ezApi.ezclocker.ezNavigation.getPublicApiServiceUrl(
            `account/ezclocker/signup?mobile=${EzBoolean.booleanOrFalse(mobile)}`);
        const signUpRequest = {
            inviteCode: null,
            accountType: 'personal',
            affiliateId: globalThis.ezApi.isValid(affiliateId)
                ? affiliateId
                : null,
            emailAddress: email,
            employerId: null,
            employerName: null,
            inviteToken: null,
            name: email,
            displayName: email,
            source: 'WEBSITE',
            businessType: null,
            pin: null,
            developerToken: null,
            password
        };

        return globalThis.ezApi.ezclocker.ezHttpHelper.ezPost(url, globalThis.ezApi.ezToJson(signUpRequest));
    }

    /**
     * @public @method
     * Creates a new account based on the aAccountType value
     * @param {string} aAccountType
     * @param {string} aName
     * @param {Number} aEmployerId
     * @param {string} aInviteToken
     * @param {string} aCompanyName
     * @param {string} aEmailAddress
     * @param {string} aPassword
     * @param {Function|null} success
     * @param {Function|null} failure
     * @returns {promise}
     * @deprecated Migrate to globalThis.ezApi.ezclocker.ezAccountServices.ezSignUp()
     */
    signUp(aAccountType, aName, aEmployerId, aInviteToken, aCompanyName, aEmailAddress, aPassword, success, failure) {
        return EzPromise.promise(
            (resolve, reject) =>
                globalThis.ezApi.ezclocker.ezAccountServices.ezSignUp(
                    // aUserRole
                    aAccountType,
                    // aUserName
                    aEmailAddress,
                    // aPassword
                    aPassword,
                    // aPin
                    null,
                    // aDisplayName
                    aName,
                    // aCompanyName
                    aCompanyName,
                    // aInviteToken
                    aInviteToken,
                    // aEmployerId
                    aEmployerId,
                    // aBusinessType
                    null,
                    // aDeveloperToken
                    null)
                    .then(
                        (response) => {
                            if (globalThis.ezApi.ezIsFunction(success)) {
                                success(response);
                            }

                            return resolve(response);
                        },
                        (eResponse) => {
                            if (globalThis.ezApi.ezIsFunction(failure)) {
                                failure(eResponse);
                            }

                            return reject(eResponse);
                        }));
    }

    /**
     * @public @method
     * Creates a new account based on the aAccountType value
     * @param {EzUserRole} ezUserRole
     * @param {string} aUserName
     * @param {string} aPassword
     * @param {string} aDisplayName
     * @param {string} aCompanyName
     * @param {Number} aEmployerId
     * @param {string} aInviteToken
     * @param {string} aDeveloperToken
     * @param {string} aEmployeeCount
     * @returns {promise}
     */
    ezSignUp(aEzUserRole, aUserName, aPassword, aPin, aDisplayName, aCompanyName, aInviteToken, aEmployerId, aBusinessType, aDeveloperToken, whereDidYouFinsUs, referralCode, aEmployeeCount) {
        if (EzUserRole.UNKNOWN === aEzUserRole) {
            throw new EzBadParamException(
                'aUserRole',
                EzAccountServices,
                EzAccountServices.ezSignUp);
        }
        if (!EzString.hasLength(aUserName)) {
            throw new EzBadParamException(
                'aUserName',
                EzAccountServices,
                EzAccountServices.ezSignUp);
        }
        if (!EzString.hasLength(aPassword)) {
            throw new EzBadParamException(
                'aPassword',
                EzAccountServices,
                EzAccountServices.ezSignUp);
        }

        return EzPromise.promise(
            (resolve, reject) => {
                // Default role to employer
                const aUserRole = EzUserRole.UNKNOWN !== aEzUserRole
                    ? aEzUserRole
                    : EzUserRole.ezAuthorityNameToRoleName(EzUserRole.ROLE_EMPLOYER);

                const em = `Signing up with account type ${aUserRole} is not supported through the website.`;

                let signUpRequest = {};

                switch (aUserRole) {
                    case 'ROLE_EMPLOYER':
                        signUpRequest = {
                            inviteToken: null,
                            inviteCode: null,
                            employerId: null,
                            developerToken: EzString.stringOrNull(aDeveloperToken),
                            accountType: EzUserRole.ezToLegacyAccountType(aUserRole),
                            emailAddress: aUserName,
                            password: aPassword,
                            pin: null,
                            name: EzString.stringWithLengthOrDefault(
                                aDisplayName,
                                aUserName),
                            displayName: EzString.stringWithLengthOrDefault(
                                aDisplayName,
                                aUserName),
                            employerName: EzString.stringWithLengthOrDefault(
                                aCompanyName,
                                'My Company'),
                            businessType: EzString.stringOrEmpty(aBusinessType),
                            whereDidYouFindUs: EzString.stringOrEmpty(whereDidYouFinsUs),
                            employeeCountAtSignup: EzString.stringOrEmpty(aEmployeeCount),
                            source: 'website',
                            referralCode:  referralCode,
                            // EW-2131: send the browser's detected time zone so the server can set
                            // the new employer's default pay-period closing date on the correct
                            // local calendar day instead of the server's UTC day.
                            targetTimeZone: globalThis.ezApi.ezclocker.ezDateTime.ezGuessLocalTimeZone()
                        };

                        break;
                    case 'ROLE_MANAGER':
                    case 'ROLE_EMPLOYEE':
                        signUpRequest = {
                            inviteToken: aInviteToken,
                            inviteCode: aInviteToken,
                            employerId: aEmployerId,
                            developerToken: EzString.hasLength(aDeveloperToken)
                                ? aDeveloperToken
                                : null,
                            accountType: EzUserRole.ezToLegacyAccountType(aUserRole),
                            emailAddress: aUserName,
                            password: aPassword,
                            pin: EzString.hasLength(aPin)
                                ? aPin
                                : null,
                            name: EzString.hasLength(aDisplayName)
                                ? aDisplayName
                                : aUserName,
                            displayName: EzString.hasLength(aDisplayName)
                                ? aDisplayName
                                : aUserName,
                            employerName: null,
                            businessType: null,
                            whereDidYouFindUs: null,
                            source: 'website',
                            referralCode:  referralCode
                        };

                        break;
                    case 'ROLE_PERSONAL':
                        signUpRequest = {
                            inviteToken: null,
                            inviteCode: null,
                            employerId: null,
                            developerToken: EzString.hasLength(aDeveloperToken)
                                ? aDeveloperToken
                                : null,
                            accountType: EzUserRole.ezToLegacyAccountType(aUserRole),
                            emailAddress: aUserName,
                            password: aPassword,
                            pin: EzString.hasLength(aPin)
                                ? aPin
                                : null,
                            name: EzString.hasLength(aDisplayName)
                                ? aDisplayName
                                : aUserName,
                            displayName: EzString.hasLength(aDisplayName)
                                ? aDisplayName
                                : aUserName,
                            employerName: null,
                            businessType: null,
                            whereDidYouFindUs: null,
                            source: 'website',
                            referralCode:  referralCode
                        };

                        break;
                    case 'ROLE_DEVELOPER':
                        signUpRequest = {
                            inviteToken: null,
                            inviteCode: null,
                            employerId: null,
                            developerToken: null,
                            accountType: EzUserRole.ezToLegacyAccountType(aUserRole),
                            emailAddress: aUserName,
                            password: aPassword,
                            pin: null,
                            name: EzString.hasLength(aDisplayName)
                                ? aDisplayName
                                : aUserName,
                            displayName: EzString.hasLength(aDisplayName)
                                ? aDisplayName
                                : aUserName,
                            employerName: null,
                            businessType: null,
                            whereDidYouFindUs: null,
                            source: 'website',
                            referralCode:  referralCode
                        };

                        break;
                    default:
                        globalThis.ezApi.ezclocker.ezLogger.error(em);

                        return reject({
                            errorCode: 400,
                            message: em
                        });
                }

                return globalThis.ezApi.ezclocker.ezHttpHelper.ezPost(
                    globalThis.ezApi.ezclocker.ezNavigation.getPublicApiServiceUrl('account/ezclocker/signup', 'v1'),
                    EzJson.toJson(signUpRequest))
                    .then(
                        globalThis.ezApi.ezclocker.ezHttpHelper.ezProcessApiResolve,
                        globalThis.ezApi.ezclocker.ezHttpHelper.ezProcessApiReject)
                    .then(resolve, reject);
            });
    }

    /**
     * @public @method
     * Processes the acceptance of an employee invite.
     * ENGINERING NOTES: Do not html encode the name or email address before calling this method. Encoding is performed automaticlly.
     * @param {string} aInviteToken
     * @param {string} aName
     * @param {string} aEmailAddress
     * @param {string} aPassword
     * @param {string} aSource
     * @returns {promise}
     */
    ezAcceptEmployeeInvite(aInviteToken, aName, aEmailAddress, aPassword, aSource) {
        if (!EzString.hasLength(aInviteToken)) {
            throw new EzBadParamException('aInviteToken',
                EzAccountServices.ezInstance,
                EzAccountServices.ezInstance.ezAcceptEmployeeInvite);
        }
        if (!EzString.hasLength(aName)) {
            throw new EzBadParamException(
                'aName',
                EzAccountServices.ezInstance,
                EzAccountServices.ezInstance.ezAcceptEmployeeInvite);
        }
        if (!EzString.hasLength(aEmailAddress)) {
            throw new EzBadParamException(
                'aEmailAddress',
                EzAccountServices.ezInstance,
                EzAccountServices.ezInstance.ezAcceptEmployeeInvite);
        }
        if (!EzString.hasLength(aPassword)) {
            throw new EzBadParamException(
                'aPassword',
                EzAccountServices.ezInstance,
                EzAccountServices.ezInstance.ezAcceptEmployeeInvite);
        }

        return globalThis.ezApi.ezclocker.ezHttpHelper.ezPost(
            globalThis.ezApi.ezclocker.ezNavigation.getPublicApiServiceUrl('account/ezclocker/accept-employee-invite', 'v1'),
            EzJson.toJson(
                {
                    inviteToken: aInviteToken,
                    name: EzString.encodeHtml(aName),
                    displayName: EzString.encodeHtml(aName),
                    emailAddress: EzString.encodeHtml(aEmailAddress),
                    password: globalThis.ezApi.ezAssignOrDefault(aPassword, null),
                    source: globalThis.ezApi.ezAssignOrDefault(aSource, 'WEBSITE')
                }))
            .then(
                globalThis.ezApi.ezclocker.ezHttpHelper.ezProcessApiResolve,
                globalThis.ezApi.ezclocker.ezHttpHelper.ezProcessApiReject);
    }

    /**
     * @public @method
     * Silently sign in a user and direct to the target url
     * @param {string} userName
     * @param {string} password
     * @param {string} mobile
     * @param {string} targetUrl
     * @param {string|null} parentContainer
     * @deprecated Migrate to globalThis.ezApi.ezclocker.ezAccountServices.ezSignIn()
     */
    silentSignIn(userName, password, mobile, targetUrl, parentContainer) {
        EzAccountServices.ezInstance.ezSignIn(
            userName,
            password,
            targetUrl,
            mobile,
            parentContainer);
    }

    /**
     * @deprecated
     * Migrate to EzAccountServices.ezInstance.ezSignIn(userName, password, targetUrl, mobile, parentContainerId)
     * @public @method
     * Silently sign in a user and direct to the target url
     * @param {string} userName
     * @param {string} password
     * @param {string} targetUrl
     * @param {string} mobile
     * @param {string|null} parentContainer
     */
    signIn(userName, password, targetUrl, mobile, parentContainer) {
        return EzAccountServices.ezInstance.ezSignIn(
            userName,
            password,
            targetUrl,
            mobile,
            parentContainer);
    }

    /**
     * @public @method
     * Silently sign in a user and direct to the target url
     * @param {string} userName
     * @param {string} password
     * @param {string} _targetUrl
     * @param {boolean} mobile
     * @param {boolean} rememberMe
     * @param {string|null} parentContainerId
     */
    ezSignIn(userName, password, _targetUrl, mobile, parentContainerId) {
        if (!EzString.hasLength(userName)) {
            throw new EzBadParamException(
                'userName',
                EzAccountServices.ezInstance,
                EzAccountServices.ezInstance.ezSignIn);
        }
        if (!EzString.hasLength(password)) {
            throw new EzBadParamException(
                'password',
                EzAccountServices.ezInstance,
                EzAccountServices.ezInstance.ezSignIn);
        }

        EzAccountServices.ezInstance.ezBuildGeneratedAutomaticSignInForm(
            globalThis.ezApi.ezclocker.ezNavigation.ezGetSigninUrl(),
            userName,
            password,
            mobile,
            EzString.stringWithLengthOrDefault(
                parentContainerId,
                'body'));

        globalThis.ezApi.ezclocker.ezUi.ezSubmitForm(EzAccountServices.ezInstance.EZ_GENERATED_AUTOMATIC_SIGN_IN_FORM_ID);
    }

    /**
     * @protected
     * Builds an HTML sign-in form used in automatic sign-in (ezSignIn)
     * @param {string} url
     * @param {string} userName
     * @param {string} password
     * @param {boolean} mobile
     * @param {string} parentContainerId
     */
    ezBuildGeneratedAutomaticSignInForm(url, userName, password, mobile, parentContainerId) {
        if (!EzString.hasLength(url)) {
            throw new EzBadParamException(
                'url',
                EzAccountServices.ezInstance,
                EzAccountServices.ezInstance.ezInjectGeneratedAutomaticSignInForm);
        }

        parentContainerId = EzString.hasLength(parentContainerId)
            ? parentContainerId
            : 'body';

        const mobileChecked = globalThis.ezApi.ezIsTrue(mobile)
            ? 'checked'
            : EzString.EMPTY;

        // ENGINERING NOTES: remember-me must always be true to make sure the authentication is re-used.
        globalThis.ezApi.ezclocker.ezUi.ezAppendContent(
            parentContainerId,
            EzHtml.build`
                <div
                    id="${EzAccountServices.ezInstance.EZ_GENERATED_AUTOMATIC_SIGN_IN_FORM_ID}Container"
                    style="display:none">
                    <form
                        id="${EzAccountServices.ezInstance.EZ_GENERATED_AUTOMATIC_SIGN_IN_FORM_ID}"
                        action="${url}"
                        method="POST">
                        <input
                            id="EzUserName"
                            type="text"
                            name="j_username"
                            value="${userName}"/>
                        <input
                            id="EzPassword"
                            type="password"
                            name="j_password"
                            value="${password}"/>
                        <input
                            id="EzRememberMe"
                            type="checkbox"
                            name="remember-me"
                            checked/>
                        <input
                            id="EzMobile"
                            type="checkbox"
                            name="mobile"
                            ${mobileChecked}/>
                    </form>
                </div>`);
    }

    /**
     * @public @method @method
     * Signs the user out of ezClocker and redirects to the sign-in page
     */
    signOut() {
        globalThis.ezApi.ezclocker.ezNavigation.ezSignOut();
    }

    /**
     * @public @method
     * Executes the call to reset the user's password to a new password.
     * @param {string} token
     * @param {string} email
     * @param {string} password
     * @param {string} confirmPassword
     * @returns {Promise)
     */
    ezResetPassword(token, email, password, confirmPassword) {
        if (!EzString.hasLength(token)) {
            throw new EzBadParamException(
                'token',
                EzAccountServices.ezInstance,
                EzAccountServices.ezInstance.ezResetPassword);
        }
        if (!EzString.hasLength(email)) {
            throw new EzBadParamException(
                'email',
                EzAccountServices.ezInstance,
                EzAccountServices.ezInstance.ezResetPassword);
        }
        if (!EzString.hasLength(password)) {
            throw new EzBadParamException(
                'password',
                EzAccountServices.ezInstance,
                EzAccountServices.ezInstance.ezResetPassword);
        }
        if (!EzString.hasLength(confirmPassword)) {
            throw new EzBadParamException(
                'confirmPassword',
                EzAccountServices.ezInstance,
                EzAccountServices.ezInstance.ezResetPassword);
        }

        return globalThis.ezApi.ezclocker.ezHttpHelper.ezPut(
            globalThis.ezApi.ezclocker.ezNavigation.ezGetPublicApiUrl(
                'account/reset-password',
                'v1'),
            EzJson.toJson({
                email,
                username: email,
                newPassword: password,
                confirmationPassword: confirmPassword,
                resetToken: token,
                source: 'WEBSITE'
            }))
            .then(
                globalThis.ezApi.ezclocker.ezHttpHelper.ezProcessApiResolve,
                globalThis.ezApi.ezclocker.ezHttpHelper.ezProcessApiReject);
    }

    /**
     * @deprecated Migrate to ezAccountServices.ezResetPassword(token, email, password, confirmPassword)
     * @public @method
     * Executes the call to reset the user's password to a new password.
     * @param {string} token
     * @param {string} email
     * @param {string} password
     * @param {string} confirmPassword
     * @param {Function|null} success
     * @param {Function|null} failure
     */
    resetPassword(token, email, password, confirmPassword, success, failure) {
        return EzAccountServices.ezInstance.ezResetPassword(token, email, password, confirmPassword)
            .then(
                (response) => globalThis.ezApi.ezCallback(success, response),
                (eResponse) => globalThis.ezApi.ezCallback(failure, eResponse));
    }

    /**
     * @deprecated
     * Migrate to globalThis.ezApi.ezclocker.ezAccountServices.ezRequestPasswordReset()
     */
    forgotPasswordReset(email, mobilePhoneNumber, success, failure) {
        EzAccountServices.ezInstance.ezRequestPasswordReset(email, mobilePhoneNumber)
            .then(success, failure);
    }

    /**
     * @deprecated
     * Migrate to globalThis.ezApi.ezclocker.ezAccountServices.ezRequestPasswordReset()
     */
    requestPasswordReset(email, success, failure) {
        EzAccountServices.ezInstance.ezRequestPasswordReset(email, EzString.EMPTY)
            .then(success, failure);
    }

    /**
     * @public @method
     * Calles the request password reset service to send a reset password link to the customer.
     * @param {string} username
     * @param {string} emailAddress
     * @param {string} mobilePhoneNumber
     * @returns {promise}
     */
    ezRequestPasswordReset(username, emailAddress, mobilePhoneNumber) {
        let params = [];
        if (EzString.hasLength(username)) {
            params.push(`un=${encodeURIComponent(username)}`);
        }
        if (EzString.hasLength(emailAddress)) {
            params.push(`em=${encodeURIComponent(emailAddress)}`);
        }
        if (EzString.hasLength(mobilePhoneNumber)) {
            params.push(`mp=${encodeURIComponent(mobilePhoneNumber)}`);
        }

        const baseUrl = globalThis.ezApi.ezclocker.ezNavigation.ezGetPublicApiUrl('account/request-reset-password');
        const url = `${baseUrl}?${params.join('&')}`;
        return globalThis.ezApi.ezclocker.ezHttpHelper.ezPost(url)
            .then(
                (response) => {
                    return globalThis.ezApi.ezclocker.ezHttpHelper.ezProcessApiResolve(response);
                },
                (error) => {
                    return globalThis.ezApi.ezclocker.ezHttpHelper.ezProcessApiReject(error);
                });
    }

    /**
     * @deprecated
     * Migrate to globalThis.ezApi.ezclocker.ezAccountServices.ezChangeUserName()
     */
    changeUserName(changeAccountEmailData, success, failure) {
        globalThis.ezApi.ezclocker.ezAccountServices.ezChangeUserName(changeAccountEmailData)
            .then(
                (response) => globalThis.ezApi.ezCallback(success, response),
                (eResponse) => globalThis.ezApi.ezCallback(failure, eResponse));
    }

    /**
     * @public @method
     * Calls the service to change the username/email of an account.
     * @param {object} changeUserNameRequest
     * @returns {promise}
     */
    ezChangeUserName(changeUserNameRequest) {
        if (!EzObject.isValid(changeUserNameRequest)) {
            throw new EzBadParamException(
                'changeUserNameRequest',
                EzAccountServices.ezInstance,
                EzAccountServices.ezInstance.ezChangeUserName);
        }

        return globalThis.ezApi.ezclocker.ezHttpHelper.ezPut(
            // url
            globalThis.ezApi.ezclocker.ezNavigation.getInternalApiServiceUrl('account/username', 'v1'),
            // payload
            EzJson.toJson(changeUserNameRequest),
            // async
            true,
            // beforeHandler
            null,
            // autoHandleResponse
            false)
            .then(
                globalThis.ezApi.ezclocker.ezHttpHelper.ezProcessApiResolve,
                globalThis.ezApi.ezclocker.ezHttpHelper.ezProcessApiReject);
    }

    /**
     * @public @method
     * Performs a sign in without redirecting the user to their dashboard.
     * @param {string} userName
     * @param {string} password
     * @returns {promise}
     */
    ezSilentSignIn(userName, password) {
        if (!EzString.hasLength(userName)) {
            throw new EzBadParamException(
                'userName',
                EzAccountServices.ezInstance,
                EzAccountServices.ezInstance.ezSilentSignIn);
        }
        if (!EzString.hasLength(password)) {
            throw new EzBadParamException(
                'password',
                EzAccountServices.ezInstance,
                EzAccountServices.ezInstance.ezSilentSignIn);
        }

        return globalThis.ezApi.ezclocker.ezHttpHelper.ezPost(
            `${globalThis.ezApi.ezclocker.ezNavigation.ezGetSigninUrl()}?j_username=${EzUrl.encodeURL(userName)}&j_password=${password}`);
    }

    /**
     * @public @method
     * Signs the user out of ezClocker (however, it does not navigate the user away from the current page)
     */
    ezSignOut() {
        globalThis.ezApi.ezclocker.ezNavigation.ezSignOut();
    }

    // ~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~
    // ~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~
    // Class Initialization and EzApi Registration Section
    // !! DO NOT PLACE ANY non-initialization fields, getter/setter methods, static methods, or class methods beyond this section !!
    // ~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~
    // ~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~

    /**
     * @static
     * @public @method @readonly @property
     * Gets the name of this class's singleton instance when registered with globalThis.ezApi.
     * @returns {string}
     */
    static get ezApiName() {
        return 'ezAccountServices';
    }

    /**
     * @static
     * @public @method @readonly @property
     * Gets an object of event names that this class may trigger.
     * @returns {object}
     */
    static get ezEventNames() {
        return {
            onReady: 'ezOn_EzAccountServices_Ready'
        };
    }

    /**
     * @static
     * @private @field
     * Stores the singleton instance of this class that was created by and registerd with EzApi.
     * @type {EzNavigation}
     */
    static #ezInstance = globalThis?.ezApi?.ezclocker?.[EzAccountServices.ezApiName]
        ? globalThis.ezApi.ezclocker[EzAccountServices.ezApiName]
        : null;
    /**
     * @static
     * @public @property @getter
     * Gets the singleton instance of this class that is registered with EzApi (if available yet)
     * @returns {EzAccountServices}
     */
    static get ezInstance() {
        return EzAccountServices.#ezInstance;
    }
    /**
     * @static
     * @public @property @setter
     * Sets the singleton instance of of this class that is registered with EzApi.
     * @param {EzAccountServices} instance
     */
    static set ezInstance(instance) {
        if (null != EzAccountServices.#ezInstance) {
            throw new Error('EzAccountServices\'s singleton instance is already registered with EzApi.');
        }

        EzAccountServices.#ezInstance = instance;
    }

    /**
     * @static
     * @private @field
     * Stores the EzApi registration state for this class.
     * Default value is NULL
     * Class is registerd with EzApi when value is EzRegistrationState.REGISTERED
     * @type {string}
     * A valid enum property value from EzRegistrationState
     */
    static #ezApiRegistrationState = globalThis?.ezApi?.ezclocker?.[EzAccountServices.ezApiName]
        ? EzRegistrationState.REGISTERED
        : EzRegistrationState.NOT_REGISTERED;
    /**
     * @static
     * @public @property @getter
     * Gets the ezApi registration state of this classes's singleton instance.
     * @returns {string}
     * A valid enum property value from EzRegistrationState
     */
    static get ezApiRegistrationState() {
        return EzAccountServices.#ezApiRegistrationState;
    }
    /**
     * @static
     * @public @property @setter
     * Sets the ezApi registration state of this classes's singleton instance.
     * @param {string} ezApiRegistrationState
     * A valid enum property value from EzRegistrationState
     */
    static set ezApiRegistrationState(ezApiRegistrationState) {
        EzAccountServices.#ezApiRegistrationState = EzRegistrationState.ezValueOf(ezApiRegistrationState);
    }

    /**
     * @static
     * @private @readonly @property
     * Gets true when all required dependencies for this class report ready.
     * In otherwords, the require dependency's singleton instance is created
     * and is registered with and accessable from EzApi with globalThis.ezApi.ezclocker.{dependency_name}.
     * @returns {boolean}
     *  ~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~
     * ENGINEERING NOTES
     * Safe to ignore SonarLint hint javascript:S1541
     * ~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~
     */
    static get #ezCanRegister() {
        return EzRegistrationState.PENDING === EzAccountServices?.ezApiRegistrationState && globalThis?.ezApi?.ready &&
            globalThis?.ezApi.ezclocker?.[EzHttpHelper.ezApiName]?.ready &&
            globalThis?.ezApi.ezclocker?.[EzNavigation.ezApiName]?.ready;
    }

    /**
     * @static
     * @private @readonly @property
     * Gets if this class's singleton instance is registered with ezApi yet.
     * @returns {boolean}
     */
    static get #ezIsRegistered() {
        return null != EzAccountServices.ezInstance &&
            EzRegistrationState.ezIsRegisteredState(EzAccountServices?.ezApiRegistrationState);
    }

    /**
     * @static
     * @private @method
     * Attempts to register the singleton instance for this class with globalThis.ezApi. Returns true
     * if successful, false otherwise.
     * @returns {boolean}
     */
    static #ezRegistrator() {
        if (EzAccountServices.#ezCanRegister && !EzAccountServices.#ezIsRegistered) {
            globalThis.ezApi.ezRegisterNewApi(
                EzAccountServices,
                EzAccountServices.ezApiName);
        }

        return EzAccountServices.#ezIsRegistered;
    }

    /**
     * @static
     * Static Initialization
     * ~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~
     * >> IMPORTANT <<
     * ~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~
     * Make sure the following properties and methods get defined BEFORE this static initialization section:
     *     1) Property getter EzAccountServices.ezApiName
     *     2) Property getter EzAccountServices.ezEventNames
     *     3) Property getter EzAccountServices.ezInstance
     *     4) Property setter EzAccountServices.ezInstance
     *     5) Property getter EzAccountServices.ezApiRegistrationState
     *     6) Property setter EzAccountServices.ezApiRegistrationState
     *     7) Property getter EzAccountServices.#ezCanRegister
     *     8) Property getter EzAccountServices.#ezIsRegistered
     *     9) Method EzAccountServices.#ezRegistrator()
     */
    static {
        if (!EzAccountServices.#ezIsRegistered) {
            EzAccountServices.ezApiRegistrationState = EzRegistrationState.PENDING;

            if (!EzAccountServices.#ezRegistrator()) {
                EzClass.ezWaitReady(
                    EzAccountServices.ezOnEzApiReadyEventName,
                    EzAccountServices.#ezRegistrator);

                EzClass.ezWaitReady(
                    EzHttpHelper.ezOnEzApiReadyEventName,
                    EzAccountServices.#ezRegistrator);

                EzClass.ezWaitReady(
                    EzNavigation.ezOnEzApiReadyEventName,
                    EzAccountServices.#ezRegistrator);
            }
        }
    }

    // ~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~
    // ~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~
    // !! DO NOT PLACE ANY non-initialization fields, getter/setter methods, static methods, or class methods beyond above section !!
    // NO CODE AFTER THIS SECTION
    // ~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~
    // ~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~
}
